import { useState } from 'react'
import { useDeviceTypes, useCreateDeviceType } from '@/api/admin'
import { useUpdateDeviceType, useDeleteDeviceType, useSSHCredentials } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'
import type { DeviceType, CreateDeviceTypeRequest, UpdateDeviceTypeRequest } from '@/types'

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

// ── Edit DeviceType Modal ────────────────────────────────────────────────────

interface EditModalProps {
  deviceType: DeviceType
  tenantId: string
  credentials: Array<{ id: string; name: string }>
  isPlatformAdmin: boolean
  onClose: () => void
}

function EditDeviceTypeModal({ deviceType, tenantId, credentials, isPlatformAdmin, onClose }: EditModalProps) {
  const update = useUpdateDeviceType(tenantId)

  const [name, setName] = useState(deviceType.name)
  const [arch, setArch] = useState(deviceType.architecture)
  const [osFamily, setOsFamily] = useState(deviceType.os_family)
  const [hawkbitDSType, setHawkbitDSType] = useState(deviceType.hawkbit_ds_type_id ?? '')
  const [defaultCredId, setDefaultCredId] = useState(deviceType.default_ssh_credential_id ?? '')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const req: UpdateDeviceTypeRequest = {}
    if (name !== deviceType.name) req.name = name
    if (arch !== deviceType.architecture) req.architecture = arch
    if (osFamily !== deviceType.os_family) req.os_family = osFamily
    if (hawkbitDSType !== (deviceType.hawkbit_ds_type_id ?? '')) req.hawkbit_ds_type_id = hawkbitDSType || undefined
    if (defaultCredId !== (deviceType.default_ssh_credential_id ?? '')) req.default_ssh_credential_id = defaultCredId || undefined
    await update.mutateAsync({ typeId: deviceType.id, req })
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Edit device type — {deviceType.name}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Architecture</label>
                <input style={inputStyle} value={arch} onChange={e => setArch(e.target.value)} placeholder="armv7l" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>OS family</label>
                <input style={inputStyle} value={osFamily} onChange={e => setOsFamily(e.target.value)} placeholder="debian" />
              </div>
            </div>
            {isPlatformAdmin && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>HawkBit DS Type ID (opzionale)</label>
                <input style={inputStyle} value={hawkbitDSType} onChange={e => setHawkbitDSType(e.target.value)} placeholder="auto-generated if empty" />
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Default SSH credential</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={defaultCredId} onChange={e => setDefaultCredId(e.target.value)}>
                <option value="">— None —</option>
                {credentials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {update.isError && (
            <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>
              {(update.error as Error).message}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export function DeviceTypes({ tenantId }: Props) {
  const { data, isLoading } = useDeviceTypes(tenantId)
  const { data: credData } = useSSHCredentials(tenantId)
  const create = useCreateDeviceType(tenantId ?? '')
  const deleteType = useDeleteDeviceType(tenantId ?? '')
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')

  const [showCreate, setShowCreate] = useState(false)
  const [editType, setEditType] = useState<DeviceType | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [arch, setArch] = useState('')
  const [osFamily, setOsFamily] = useState('')

  function resetForm() {
    setName('')
    setArch('')
    setOsFamily('')
    setShowCreate(false)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const req: CreateDeviceTypeRequest = { name: name.trim(), architecture: arch.trim(), os_family: osFamily.trim() }
    await create.mutateAsync(req)
    resetForm()
  }

  const types = data?.data ?? []
  const credentials = credData?.data.map(c => ({ id: c.id, name: c.name })) ?? []
  const hasNoCredentials = !isLoading && credData !== undefined && credentials.length === 0

  return (
    <div className="page-container">
      {editType && tenantId && (
        <EditDeviceTypeModal
          deviceType={editType}
          tenantId={tenantId}
          credentials={credentials}
          isPlatformAdmin={isPlatformAdmin}
          onClose={() => setEditType(null)}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Device Types</h1>
          <p className="page-subtitle">{types.length} total</p>
        </div>
        <RequirePermission perm="devices:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(v => !v)}>
            {showCreate ? 'Cancel' : '+ Add type'}
          </button>
        </RequirePermission>
      </div>

      {hasNoCredentials && tenantId && (
        <div style={{
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 'var(--radius-sm)',
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 13,
          color: '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          No SSH credentials configured yet.
          Device provisioning requires at least one SSH credential for the AWX bootstrap.{' '}
          <a href="/credentials" style={{ color: 'inherit', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
            Create SSH credentials
          </a>{' '}
          before registering devices.
        </div>
      )}

      {showCreate && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>New device type</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name *</label>
                <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="modberry-500" required />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Architecture *</label>
                <input style={inputStyle} value={arch} onChange={e => setArch(e.target.value)} placeholder="armv7l" required />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>OS family *</label>
                <input style={inputStyle} value={osFamily} onChange={e => setOsFamily(e.target.value)} placeholder="debian" required />
              </div>
            </div>
            {create.isError && (
              <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>
                {(create.error as Error).message}
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </form>
        </div>
      )}

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      ) : types.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>No device types defined yet.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Architecture</th>
                <th>OS family</th>
                <th>Status</th>
                {isPlatformAdmin && <th>HawkBit DS type</th>}
                <th>Default SSH cred</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {types.map(dt => (
                <tr key={dt.id}>
                  <td style={{ fontWeight: 500 }}>{dt.name}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{dt.architecture}</td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{dt.os_family}</td>
                  <td>
                    {dt.hawkbit_ds_type_id
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3, background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>● Synced</span>
                      : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3, background: 'rgba(234,179,8,0.12)', color: '#facc15', border: '1px solid rgba(234,179,8,0.3)' }}>⟳ Pending</span>
                    }
                  </td>
                  {isPlatformAdmin && (
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {dt.hawkbit_ds_type_id ?? '—'}
                    </td>
                  )}
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {dt.default_ssh_credential_id
                      ? (credentials.find(c => c.id === dt.default_ssh_credential_id)?.name ?? dt.default_ssh_credential_id)
                      : '—'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {new Date(dt.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <RequirePermission perm="devices:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditType(dt)}
                        >
                          Edit
                        </button>
                      </RequirePermission>
                      <RequirePermission perm="devices:delete">
                        {confirmDeleteId === dt.id ? (
                          <span style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>Delete?</span>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '4px 8px', fontSize: 12, color: '#ef4444' }}
                              disabled={deleteType.isPending}
                              onClick={() => {
                                deleteType.mutate(dt.id)
                                setConfirmDeleteId(null)
                              }}
                            >
                              Confirm
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '4px 8px', fontSize: 12 }}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => setConfirmDeleteId(dt.id)}
                          >
                            Delete
                          </button>
                        )}
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
